import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import { Navigate, type RouteObject, createBrowserRouter } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/layout/RouteErrorBoundary";
import { RouteFallback } from "@/components/layout/RouteFallback";
import { TabGuard } from "@/components/layout/TabGuard";
import { TabIndexRedirect } from "@/components/layout/TabIndexRedirect";
import { MODULES } from "@/lib/modules";
import { moduleAllRoutes } from "@/lib/module-registry";
import { recordRoutes } from "@/lib/nav-records";
import { APP_ROUTES, routeMetaFor, type AppRoute } from "@/lib/routes";

/**
 * Loader for the wildcard route. Throws a 404 Response which the shell-level
 * `<RouteErrorBoundary>` catches via `isRouteErrorResponse` and renders as
 * the standard "Page not found" page.
 *
 * A `loader` is the supported place to throw a Response — throwing from a
 * component's render bubbles as a generic JS exception, which the boundary
 * mis-classifies as a runtime error.
 */
function notFoundLoader(): never {
  throw new Response("Not Found", { status: 404, statusText: "Not Found" });
}

/**
 * Data router configuration. Using `createBrowserRouter` (the data router)
 * instead of `<BrowserRouter>` because the data router is the modern RRv6
 * API: it supports `handle` on route objects, `loader`/`action` for data
 * fetching, and `useMatches()` returns routes with their `handle` attached.
 *
 * The old declarative `<Routes>` / `<BrowserRouter>` pattern is kept around
 * for backwards compat but doesn't expose route `handle` — so we committed
 * to the data router up front.
 */
/**
 * Materialize one `AppRoute` into zero, one, or several `RouteObject`s.
 *
 * - **Standard route** (`element` set): one `RouteObject` at its path,
 *   wrapped in `<ProtectedRoute>`.
 * - **Tabbed parent** (`children` set): same, plus nested child routes.
 * - **Sidebar grouping node** (`subItems` set, no `element`): the parent
 *   itself produces no route; each `subItem` recurses through this
 *   function so it's registered as a standalone route at its full path.
 *
 * Sidebar grouping nodes exist purely for nav structure — clicking the
 * parent in the sidebar only expands/collapses; there's no page to
 * route to. The `subItems` carry their own auth gating and breadcrumbs.
 */
function buildRoute(r: AppRoute): RouteObject[] {
  if (r.subItems && r.subItems.length > 0 && !r.element) {
    // Pure grouping node — recurse into each subItem so they register
    // as standalone routes. The parent contributes nothing to the
    // router; its only job is the sidebar expander entry.
    return r.subItems.flatMap(buildRoute);
  }

  const base: RouteObject = {
    path: r.path,
    // ProtectedRoute is a no-op for routes without a permission/feature/
    // super-admin gate, so wrapping unconditionally keeps the config uniform.
    // The page
    // elements are lazy (see `lib/routes.tsx`), so a `<Suspense>` boundary
    // renders `<RouteFallback>` while a page's chunk downloads.
    element: (
      <ProtectedRoute route={r}>
        <Suspense fallback={<RouteFallback />}>{r.element}</Suspense>
      </ProtectedRoute>
    ),
    handle: {
      meta: routeMetaFor(r),
      chromeless: r.chromeless ?? false,
      bare: r.bare ?? false,
      // Stays open in Demo Mode (see `AppRoute.demoSafe`).
      demoSafe: r.demoSafe ?? false,
      // Which module's chrome wraps this route. Absent on shared record routes
      // (clients, accounts, …) — those render in whichever app the user is
      // already in, resolved from nav context in `AppShell`, which is what
      // keeps a drill-in from being a module switch.
      moduleKey: r.moduleKey,
    },
  };

  if (r.children && r.children.length > 0) {
    // Parent renders <RouterTabs /> + <Outlet />; each child is a nested
    // route whose element mounts at /parent/:child-path. Visiting the
    // parent path alone hits the index route, which redirects to the
    // caller's first authorized, non-disabled child tab. The redirect is
    // session-aware (see TabIndexRedirect) — a static "first non-disabled"
    // redirect would loop with <TabGuard> when the caller lacks that tab's
    // permission (e.g. Contacts access removed on the client detail page).
    base.children = [
      {
        index: true,
        element: <TabIndexRedirect tabs={r.children} />,
      } satisfies RouteObject,
      ...r.children
        .filter((c) => !c.disabled)
        .map<RouteObject>((c) => ({
          // Nested tabs register as `<path>/*` so React Router matches
          // both `/parent/tab` and any deeper `/parent/tab/anything`
          // URL — the tab component owns the sub-route parsing.
          path: c.nested ? `${c.path}/*` : c.path,
          // Wrap in <TabGuard> so a deep link to a tab the caller lacks the
          // permission/feature for bounces to the parent instead of rendering
          // the element (and 403-ing on its API call). No-op for ungated tabs.
          element: <TabGuard tab={c}>{c.element}</TabGuard>,
        })),
    ];
  }

  return [base];
}

/**
 * Every shell-wrapped route, deduped by path.
 *
 * Dedupe is load-bearing: a shared record module is registered canonically
 * by `recordRoutes()` AND composed into the nav of every module that surfaces
 * it (Clients appears in both Sales' and Admin's), so the same `/clients`
 * path is emitted several times. React Router would silently match only the
 * first, but registering duplicates is still a bug — the extra copies are
 * dead config that drifts.
 *
 * `recordRoutes()` goes first so the canonical, app-agnostic registration
 * wins. That matters: those entries carry no `moduleKey`, so a record renders
 * in whichever app the user is already in (chrome comes from nav context —
 * see `recordNavRoute` and `lib/module-context.ts`). If a module's nav copy won
 * instead, it would stamp its own `moduleKey` and drilling into a record would
 * yank the user into that app.
 */
function shellRoutes(): AppRoute[] {
  const seen = new Set<string>();
  const out: AppRoute[] = [];
  const push = (routes: AppRoute[]) => {
    for (const r of routes) {
      if (r.status !== "live" || r.public) continue;
      // A cross-link is a sidebar row pointing at another module's page. It
      // carries no element, and reserving its path here would claim the slot
      // before the owning module registers the real one — the dedupe keeps
      // the FIRST entry, so the page would render as nothing.
      if (r.crossLink) continue;
      if (seen.has(r.path)) continue;
      seen.add(r.path);
      // **Record `subItems` paths too — but only for a PURE grouping node.**
      // `buildRoute` recurses into `subItems` and registers each child at its
      // full path ONLY when the parent carries no `element` of its own (see
      // that function's own condition, `!r.element`); a parent that IS also a
      // page (Market Intelligence's flat gas "Pricing points" row was one,
      // before gas had regions: a real page element AND `subItems` for the
      // sidebar tree) is a single route here, and its `subItems` are
      // sidebar-only decoration whose real routes are registered elsewhere
      // (that row's per-hub `hiddenRoutes`). Reserving those
      // child paths in `seen` before the real per-hub routes were pushed made
      // every one of them silently lose to this loop's own dedup, 404-ing
      // navigation into any gas hub even though the URL and the component
      // both existed.
      //
      // For an actual pure grouping node — tracking only `r.path` left its
      // children undeduped. Four settings pages
      // (`/settings/portal-links`, `/settings/matrix-adder-defaults`,
      // `/settings/passthrough-rates`, `/settings/scoring-model`) are declared
      // under a module's settings group AND under legacy's Admin group; the
      // two *parents* have distinct paths, so `seen` never collided and each
      // child was emitted twice.
      //
      // React Router matches the first, so nothing broke — but the docblock
      // above calls duplicate registration "dead config that drifts", and
      // that guarantee has to reach child paths to be true. The module copy
      // wins in both flag states, so editing only the legacy copy of one of
      // these would silently do nothing.
      //
      // Both directions matter. A child can also collide with a *top-level*
      // route registered earlier: `/users` and `/segments` are plain routes
      // in the Admin module and `subItems` of legacy's `/access` grouping
      // node. So drop children already seen, and record the rest.
      if (r.subItems?.length && !r.element) {
        const fresh = r.subItems.filter((sub) => !seen.has(sub.path));
        for (const sub of fresh) seen.add(sub.path);
        // A grouping node whose children were all claimed elsewhere would
        // register nothing — skip it rather than emitting an empty parent.
        if (fresh.length === 0) continue;
        out.push({ ...r, subItems: fresh });
        continue;
      }
      out.push(r);
    }
  };
  // Shared record routes first — one canonical registration each (list +
  // detail), independent of whether any app puts them in a sidebar.
  push(recordRoutes());
  for (const app of MODULES) push(moduleAllRoutes(app));
  return out;
}

// Wrap the data-router factory so React Router v7 navigations produce Sentry
// performance transactions. The `reactRouterV7BrowserTracingIntegration`
// registered in `@/lib/sentry` supplies the matching instrumentation.
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  [
    // Public routes — no shell, no session dependency. Used for the SPA's
    // login + tenant-select pages, which must render while signed out.
    // Listed first so they match before the shell-wrapped wildcard.
    ...APP_ROUTES.filter((r) => r.status === "live" && r.public).map<RouteObject>((r) => ({
      path: r.path,
      // Public pages are lazy too — wrap in Suspense so the chunk loads
      // outside the shell (these routes don't reach the AppShell Outlet).
      element: <Suspense fallback={<RouteFallback />}>{r.element}</Suspense>,
      errorElement: <RouteErrorBoundary />,
      handle: { meta: routeMetaFor(r) },
    })),
    {
      element: <AppShell />,
      // App-wide error boundary. Attached at the shell level so the
      // sidebar + topbar stay rendered when a child route throws —
      // the user always has a way to navigate out of the error state.
      // `RouteErrorBoundary` handles both route-response errors
      // (404/403/500) and runtime exceptions thrown during render.
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: "/", element: <Navigate to="/dashboard" replace /> },
        ...shellRoutes().flatMap(buildRoute),
        { path: "*", loader: notFoundLoader },
      ],
    },
  ],
  { basename: "/" },
);
